import React, { Component } from 'react';
import { X, Send, Reply, Paperclip, Image, Video, File } from 'lucide-react';
import { apiService } from '@/services/api';
import { tournamentChatWebSocketService } from '@/services/tournamentChatWebSocket';
import { getUserIdFromToken } from '@/utils/jwt';
import { cleanUUID, isValidUUID } from '@/utils/uuid';
import { linkifyText } from '@/utils/linkify';
import styles from '../BookingChat/BookingChat.module.scss';

interface TournamentChatProps {
  tournamentId: string;
  tournamentName: string;
  organizerName: string;
  onClose: () => void;
  /** Called after conversation is opened and marked as read; use to clear unread badge */
  onMarkedAsRead?: () => void;
}

interface ReplyToMessage {
  id: string;
  messageText: string;
  senderType: 'customer' | 'organizer';
  senderId: string;
  createdAt: string;
}

interface TournamentMessageAttachment {
  id: string;
  fileType: 'image' | 'video' | 'audio' | 'document' | 'link';
  fileUrl: string;
  fileName?: string | null;
  fileSize?: number | null;
  mimeType?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

interface TournamentChatMessage {
  id: string;
  senderId: string;
  senderType: 'customer' | 'organizer';
  senderName: string;
  message: string;
  timestamp: string;
  read: boolean;
  replyToMessage?: ReplyToMessage | null;
  attachments?: TournamentMessageAttachment[];
}

interface TournamentChatState {
  conversationId: string | null;
  messages: TournamentChatMessage[];
  inputMessage: string;
  isLoading: boolean;
  isSending: boolean;
  isTyping: boolean;
  organizerTyping: boolean;
  error: string | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  chatAvailable: boolean;
  replyingTo: TournamentChatMessage | null;
  selectedFiles: File[];
  isUploading: boolean;
}

function mapApiMessageToUi(msg: any, currentUserId: string, organizerName: string): TournamentChatMessage {
  const senderId = msg.senderId || msg.sender?.id;
  const isCustomer = senderId === currentUserId;
  return {
    id: msg.id,
    senderId,
    senderType: isCustomer ? 'customer' : 'organizer',
    senderName: isCustomer ? 'You' : (msg.sender?.displayName || msg.sender?.identifier || organizerName),
    message: msg.messageText || msg.message || '',
    timestamp: msg.createdAt || new Date().toISOString(),
    read: msg.isRead || false,
    replyToMessage: msg.replyToMessage
      ? {
          id: msg.replyToMessage.id,
          messageText: msg.replyToMessage.messageText || msg.replyToMessage.message,
          senderType: msg.replyToMessage.senderId === currentUserId ? 'customer' : 'organizer',
          senderId: msg.replyToMessage.senderId,
          createdAt: msg.replyToMessage.createdAt
        }
      : null,
    attachments:
      msg.attachments && Array.isArray(msg.attachments)
        ? msg.attachments.map((a: any) => ({
            id: a.id,
            fileType: a.fileType || 'document',
            fileUrl: a.fileUrl,
            fileName: a.fileName,
            fileSize: a.fileSize,
            mimeType: a.mimeType,
            metadata: a.metadata,
            createdAt: a.createdAt
          }))
        : undefined
  };
}

function mapAttachmentToRenderType(fileType: string): 'image' | 'video' | 'file' | 'link' {
  if (fileType === 'image') return 'image';
  if (fileType === 'video') return 'video';
  if (fileType === 'link') return 'link';
  return 'file';
}

/** If URL is YouTube or Vimeo, return embed URL so it can play inside the chat. */
function getVideoEmbedUrl(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  const ytMatch = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/i);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=0`;
  const vimeoMatch = s.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

class TournamentChat extends Component<TournamentChatProps, TournamentChatState> {
  private messagesEndRef: React.RefObject<HTMLDivElement>;
  private chatContainerRef: React.RefObject<HTMLDivElement>;
  private fileInputRef: React.RefObject<HTMLInputElement>;
  private typingTimeout: ReturnType<typeof setTimeout> | null = null;
  private currentUserId: string | null = null;

  constructor(props: TournamentChatProps) {
    super(props);
    this.state = {
      conversationId: null,
      messages: [],
      inputMessage: '',
      isLoading: true,
      isSending: false,
      isTyping: false,
      organizerTyping: false,
      error: null,
      connectionStatus: 'connecting',
      chatAvailable: false,
      replyingTo: null,
      selectedFiles: [],
      isUploading: false
    };
    this.messagesEndRef = React.createRef();
    this.chatContainerRef = React.createRef();
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    const { tournamentId } = this.props;
    const tid = cleanUUID(tournamentId);
    if (!tid || !isValidUUID(tid)) {
      this.setState({ error: 'Invalid tournament ID.', isLoading: false });
      return;
    }
    const { accessToken } = apiService.getTokens();
    this.currentUserId = getUserIdFromToken(accessToken);
    this.openConversation();
  }

  componentWillUnmount() {
    this.removeSocketListeners();
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    const { conversationId } = this.state;
    if (conversationId) {
      tournamentChatWebSocketService.leaveConversation(conversationId);
    }
  }

  componentDidUpdate(_prevProps: TournamentChatProps, prevState: TournamentChatState) {
    if (this.state.messages.length !== prevState.messages.length) {
      this.scrollToBottom();
    }
  }

  openConversation = async () => {
    const { tournamentId, organizerName } = this.props;
    const tid = cleanUUID(tournamentId);
    if (!tid) return;

    this.setState({ isLoading: true, error: null });

    try {
      const response = await apiService.getTournamentChatOneToOne(tid, { messageLimit: 50 });
      if (!response.success) {
        throw new Error((response as any).message || 'Failed to load chat');
      }

      const conversation = (response as any).conversation;
      const messages = (response as any).messages || [];
      const totalMessages = (response as any).totalMessages ?? 0;

      if (!conversation || !conversation.id) {
        this.setState({
          isLoading: false,
          chatAvailable: false,
          error: 'Could not open chat. Please try again.'
        });
        return;
      }

      const conversationId = conversation.id;
      const uiMessages: TournamentChatMessage[] = (messages as any[])
        .map((m) => mapApiMessageToUi(m, this.currentUserId || '', organizerName))
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      this.setState({
        conversationId,
        messages: uiMessages,
        isLoading: false,
        chatAvailable: true,
        connectionStatus: 'connecting'
      });

      tournamentChatWebSocketService.connect(conversationId);
      this.setupSocketListeners(conversationId);

      // Mark conversation as read and clear unread badge (see TOURNAMENT_CHAT_NOTIFICATIONS.md)
      try {
        await apiService.markTournamentChatAsRead(tid, conversationId);
        this.props.onMarkedAsRead?.();
      } catch (_e) {
        // Non-blocking; badge may still clear when user reads
      }
    } catch (err: any) {
      const message = err?.message || 'Failed to open chat';
      const is403 = (err?.message || '').toLowerCase().includes('not registered') || (err?.message || '').toLowerCase().includes('no longer available');
      this.setState({
        isLoading: false,
        chatAvailable: false,
        error: is403 ? message : 'Could not load chat. You must be registered for this tournament and it must not be completed or cancelled.'
      });
    }
  };

  handleJoined = () => {
    this.setState({ connectionStatus: 'connected' });
  };

  handleDisconnect = () => {
    this.setState({ connectionStatus: 'disconnected' });
  };

  setupSocketListeners = (conversationId: string) => {
    tournamentChatWebSocketService.on('tournament_chat:joined', this.handleJoined);
    tournamentChatWebSocketService.on('tournament_chat:message', this.handleNewMessage);
    tournamentChatWebSocketService.on('tournament_chat:typing', this.handleTyping);
    tournamentChatWebSocketService.on('tournament_chat:notification', this.handleNotification);
    tournamentChatWebSocketService.on('error', this.handleSocketError);
    tournamentChatWebSocketService.on('disconnect', this.handleDisconnect);
  };

  removeSocketListeners = () => {
    tournamentChatWebSocketService.off('tournament_chat:joined', this.handleJoined);
    tournamentChatWebSocketService.off('tournament_chat:message', this.handleNewMessage);
    tournamentChatWebSocketService.off('tournament_chat:typing', this.handleTyping);
    tournamentChatWebSocketService.off('tournament_chat:notification', this.handleNotification);
    tournamentChatWebSocketService.off('error', this.handleSocketError);
    tournamentChatWebSocketService.off('disconnect', this.handleDisconnect);
  };

  handleNewMessage = (data: { conversationId: string; message?: any }) => {
    const { conversationId } = this.state;
    const { organizerName } = this.props;
    if (data.conversationId !== conversationId || !data.message) return;

    const msg = data.message;
    const messageId = msg.id;
    if (!messageId) return;

    const senderId = msg.senderId || msg.sender?.id;
    const isFromCurrentUser = senderId === this.currentUserId;

    this.setState((prevState) => {
      // Already have this message (e.g. we added it from API response already)
      if (prevState.messages.some((m) => m.id === messageId)) return prevState;

      const newMsg = mapApiMessageToUi(msg, this.currentUserId || '', organizerName);

      // Message we sent: replace optimistic (temp) message with this one to avoid duplicate
      if (isFromCurrentUser) {
        const tempIndex = prevState.messages.findIndex(
          (m) => m.id.startsWith('temp-') && m.senderType === 'customer'
        );
        if (tempIndex !== -1) {
          const updated = [...prevState.messages];
          updated[tempIndex] = newMsg;
          return {
            ...prevState,
            messages: updated.sort(
              (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            ),
            organizerTyping: false
          };
        }
      }

      // New message from organizer or no temp to replace: append
      const updated = [...prevState.messages, newMsg].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      return { ...prevState, messages: updated, organizerTyping: false };
    });
  };

  handleTyping = (data: { conversationId: string; userId: string; isTyping: boolean }) => {
    const { conversationId } = this.state;
    if (data.conversationId !== conversationId) return;
    if (data.userId === this.currentUserId) return;
    this.setState({ organizerTyping: data.isTyping });
    if (data.isTyping) {
      setTimeout(() => this.setState({ organizerTyping: false }), 3000);
    }
  };

  handleNotification = (_data: any) => {
    // Optional: show badge or toast when chat is closed
  };

  handleSocketError = () => {
    this.setState({ connectionStatus: 'error', error: 'Connection error. Please refresh.' });
  };

  scrollToBottom = () => {
    this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  loadMoreMessages = async () => {
    const { tournamentId } = this.props;
    const { conversationId, messages } = this.state;
    if (!conversationId || messages.length === 0) return;
    const oldestId = messages[0]?.id;
    if (!oldestId || oldestId.startsWith('temp-')) return;

    try {
      const response = await apiService.getTournamentChatMessages(
        cleanUUID(tournamentId)!,
        conversationId,
        { messageLimit: 50, beforeMessageId: oldestId }
      );
      const list = (response as any).messages || [];
      if (list.length === 0) return;
      const { organizerName } = this.props;
      const older = list.map((m: any) => mapApiMessageToUi(m, this.currentUserId || '', organizerName));
      this.setState((prev) => ({
        messages: [...older, ...prev.messages].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
      }));
    } catch (_e) {
      // ignore
    }
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState({ inputMessage: value });
    const { conversationId } = this.state;
    if (!conversationId) return;
    if (value.trim()) {
      tournamentChatWebSocketService.startTyping(conversationId);
      if (this.typingTimeout) clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        tournamentChatWebSocketService.stopTyping(conversationId);
        this.typingTimeout = null;
      }, 2000);
    } else {
      tournamentChatWebSocketService.stopTyping(conversationId);
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }
    }
  };

  handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const { tournamentId } = this.props;
    const { conversationId, inputMessage, replyingTo, selectedFiles, isSending } = this.state;
    const tid = cleanUUID(tournamentId);
    if (!tid || !conversationId || isSending) return;
    if (!inputMessage.trim() && selectedFiles.length === 0) return;

    const messageText = inputMessage.trim() || '';
    const tempId = `temp-${Date.now()}`;
    const optimistic: TournamentChatMessage = {
      id: tempId,
      senderId: this.currentUserId || 'customer',
      senderType: 'customer',
      senderName: 'You',
      message: messageText,
      timestamp: new Date().toISOString(),
      read: false,
      replyToMessage: replyingTo
        ? {
            id: replyingTo.id,
            messageText: replyingTo.message,
            senderType: replyingTo.senderType,
            senderId: replyingTo.senderId,
            createdAt: replyingTo.timestamp
          }
        : null,
      attachments:
        selectedFiles.length > 0
          ? selectedFiles.map((file, i) => ({
              id: `temp-${tempId}-${i}`,
              fileType: 'document' as const,
              fileUrl: URL.createObjectURL(file),
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type,
              createdAt: new Date().toISOString()
            }))
          : undefined
    };

    this.setState((prev) => ({
      messages: [...prev.messages, optimistic],
      inputMessage: '',
      isSending: true,
      isTyping: false,
      replyingTo: null,
      isUploading: selectedFiles.length > 0
    }));
    tournamentChatWebSocketService.stopTyping(conversationId);
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    try {
      const payload: { messageText?: string; replyToMessageId?: string; attachments?: File[] } = {};
      if (messageText) payload.messageText = messageText;
      if (replyingTo?.id && !replyingTo.id.startsWith('temp-')) payload.replyToMessageId = replyingTo.id;
      if (selectedFiles.length > 0) payload.attachments = selectedFiles;

      const response = await apiService.sendTournamentChatMessage(tid, conversationId, payload);
      const created = (response as any).message || (response as any).data?.message;
      if (created) {
        const { organizerName } = this.props;
        const newMsg = mapApiMessageToUi(created, this.currentUserId || '', organizerName);
        this.setState((prev) => ({
          messages: prev.messages.map((m) => (m.id === tempId ? newMsg : m)),
          isSending: false,
          isUploading: false,
          selectedFiles: []
        }));
      } else {
        this.setState({ isSending: false, isUploading: false, selectedFiles: [] });
      }
      if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    } catch (err: any) {
      this.setState((prev) => ({
        messages: prev.messages.filter((m) => m.id !== tempId),
        isSending: false,
        isUploading: false,
        error: err?.message || 'Failed to send message.'
      }));
      setTimeout(() => this.setState({ error: null }), 5000);
    }
  };

  formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  handleReplyClick = (message: TournamentChatMessage) => {
    this.setState({ replyingTo: message });
  };

  scrollToMessage = (messageId: string) => {
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'auto', block: 'center' });
      messageElement.classList.add(styles.messageHighlightBlink);
      setTimeout(() => {
        messageElement.classList.remove(styles.messageHighlightBlink);
      }, 650);
    }
  };

  handleCancelReply = () => {
    this.setState({ replyingTo: null });
  };

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const maxSize = 50 * 1024 * 1024;
    const valid = files.filter((f) => f.size <= maxSize);
    this.setState((prev) => {
      const combined = [...prev.selectedFiles, ...valid].slice(0, 10);
      return { selectedFiles: combined };
    });
  };

  handleRemoveFile = (index: number) => {
    this.setState((prev) => ({ selectedFiles: prev.selectedFiles.filter((_, i) => i !== index) }));
  };

  formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  /** Resolve URL so relative paths open correctly in new tab (same origin). */
  resolveMediaUrl = (url: string): string => {
    if (!url || typeof url !== 'string') return url;
    const s = url.trim();
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    if (typeof window !== 'undefined' && window.location?.origin) {
      return s.startsWith('/') ? window.location.origin + s : window.location.origin + '/' + s;
    }
    return s;
  };

  renderAttachment = (att: TournamentMessageAttachment) => {
    const type = mapAttachmentToRenderType(att.fileType);
    const resolvedUrl = this.resolveMediaUrl(att.fileUrl);
    if (type === 'image') {
      return (
        <div key={att.id} className={styles.attachment}>
          <div className={styles.attachmentImage}>
            <img src={resolvedUrl} alt={att.fileName || 'Image'} onClick={() => window.open(resolvedUrl, '_blank', 'noopener,noreferrer')} loading="lazy" />
            {att.fileName && <div className={styles.attachmentFileName}>{att.fileName}</div>}
          </div>
        </div>
      );
    }
    if (type === 'video') {
      const embedUrl = getVideoEmbedUrl(resolvedUrl);
      return (
        <div key={att.id} className={styles.attachment}>
          <div className={styles.attachmentVideo}>
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title={att.fileName || 'Video'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', maxWidth: '100%', aspectRatio: '16/9', maxHeight: '400px', border: 'none', borderRadius: 8 }}
              />
            ) : (
              <video
                controls
                preload="metadata"
                playsInline
                src={resolvedUrl}
                style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: 8 }}
              />
            )}
            {att.fileName && <div className={styles.attachmentFileName}>{att.fileName}</div>}
          </div>
        </div>
      );
    }
    if (type === 'link') {
      return (
        <div key={att.id} className={styles.attachment}>
          <a
            href={resolvedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.attachmentLink}
            onClick={(e) => { e.preventDefault(); window.open(resolvedUrl, '_blank', 'noopener,noreferrer'); }}
          >
            🔗 {att.fileUrl}
          </a>
        </div>
      );
    }
    return (
      <div key={att.id} className={styles.attachment}>
        <a
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.attachmentFile}
          onClick={(e) => { e.preventDefault(); window.open(resolvedUrl, '_blank', 'noopener,noreferrer'); }}
        >
          <File size={20} />
          <div className={styles.attachmentFileInfo}>
            <div className={styles.attachmentFileName}>{att.fileName || 'Download'}</div>
            {att.fileSize != null && <div className={styles.attachmentFileSize}>{this.formatFileSize(att.fileSize)}</div>}
          </div>
        </a>
      </div>
    );
  };

  render() {
    const { tournamentName, organizerName, onClose } = this.props;
    const {
      conversationId,
      messages,
      inputMessage,
      isLoading,
      isSending,
      isUploading,
      organizerTyping,
      error,
      connectionStatus,
      chatAvailable,
      replyingTo,
      selectedFiles
    } = this.state;

    const containerClass = styles.bookingChatContainer;

    if (!chatAvailable && !isLoading) {
      return (
        <div className={containerClass}>
          <div className={styles.chatHeader}>
            <div className={styles.chatHeaderInfo}>
              <h3>Chat with organizer</h3>
            </div>
            <button type="button" className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className={styles.chatMessages}>
            <div className={styles.emptyChat}>
              <p>{error || 'Chat is not available for this tournament.'}</p>
              <p className={styles.emptyChatHint}>You must be registered and the tournament must not be completed or cancelled.</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={containerClass}>
        <div className={styles.chatHeader}>
          <div className={styles.chatHeaderInfo}>
            <div className={styles.chatAvatar}>🏆</div>
            <div className={styles.chatUserInfo}>
              <h3>{organizerName}</h3>
              <div className={styles.chatHeaderMeta}>
                <span className={styles.chatSubtitle}>{tournamentName}</span>
                <span className={`${styles.connectionStatus} ${styles[connectionStatus]}`}>
                  {connectionStatus === 'connected' && '🟢'}
                  {connectionStatus === 'connecting' && '🟡'}
                  {(connectionStatus === 'disconnected' || connectionStatus === 'error') && '🔴'}
                </span>
              </div>
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.chatMessages} ref={this.chatContainerRef}>
          {isLoading ? (
            <div className={styles.emptyChat}>
              <div className={styles.loadingSpinner} />
              <p>Loading chat...</p>
            </div>
          ) : !conversationId ? null : messages.length === 0 ? (
            <div className={styles.emptyChat}>
              <p>Start a conversation with {organizerName}</p>
              <p className={styles.emptyChatHint}>Ask about the tournament, rules, or schedule.</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  data-message-id={message.id}
                  className={`${styles.messageBubble} ${
                    message.senderType === 'customer' ? styles.messageCustomer : styles.messageOrganizer
                  } ${message.id.startsWith('temp-') ? styles.messagePending : ''}`}
                >
                  {message.senderType === 'organizer' && <div className={styles.messageSender}>{message.senderName}</div>}
                  {message.replyToMessage && (
                    <div
                      className={styles.replyPreview}
                      onClick={() => this.scrollToMessage(message.replyToMessage!.id)}
                      onKeyDown={(e) => e.key === 'Enter' && this.scrollToMessage(message.replyToMessage!.id)}
                      role="button"
                      tabIndex={0}
                      title="Click to view original message"
                    >
                      <div className={styles.replyPreviewContent}>
                        <div className={styles.replyPreviewSender}>
                          {message.replyToMessage.senderType === 'customer' ? 'You' : organizerName}
                        </div>
                        <div className={styles.replyPreviewText}>
                          {message.replyToMessage.messageText.length > 50
                            ? `${message.replyToMessage.messageText.slice(0, 50)}...`
                            : message.replyToMessage.messageText}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={styles.messageText}>{linkifyText(message.message)}</div>
                  {message.attachments && message.attachments.length > 0 && (
                    <div className={styles.messageAttachments}>
                      {message.attachments.map((a) => this.renderAttachment(a))}
                    </div>
                  )}
                  <div className={styles.messageFooter}>
                    <div className={styles.messageTime}>{this.formatTime(message.timestamp)}</div>
                    {message.senderType === 'organizer' && (
                      <button type="button" className={styles.replyButton} onClick={() => this.handleReplyClick(message)} title="Reply">
                        <Reply size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {organizerTyping && (
                <div className={styles.typingIndicator}>
                  <div className={styles.typingDots}>
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className={styles.typingText}>{organizerName} is typing...</span>
                </div>
              )}
            </>
          )}
          <div ref={this.messagesEndRef} />
        </div>

        {replyingTo && (
          <div className={styles.replyIndicator}>
            <div className={styles.replyIndicatorContent}>
              <div className={styles.replyIndicatorInfo}>
                <span className={styles.replyLabel}>Replying to:</span>
                <span className={styles.replyText}>
                  {replyingTo.message.length > 50 ? `${replyingTo.message.slice(0, 50)}...` : replyingTo.message}
                </span>
              </div>
              <button type="button" className={styles.cancelReplyButton} onClick={this.handleCancelReply} title="Cancel reply">
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className={styles.selectedFilesPreview}>
            <div className={styles.selectedFilesHeader}>
              <span className={styles.selectedFilesCount}>{selectedFiles.length} file(s) selected</span>
              <button
                type="button"
                className={styles.clearAllFilesButton}
                onClick={() => {
                  this.setState({ selectedFiles: [] });
                  if (this.fileInputRef.current) this.fileInputRef.current.value = '';
                }}
              >
                Clear All
              </button>
            </div>
            <div className={styles.selectedFilesList}>
              {selectedFiles.map((file, index) => (
                <div key={index} className={styles.selectedFileItem}>
                  <div className={styles.selectedFileIcon}>
                    {file.type.startsWith('image/') ? <Image size={16} /> : file.type.startsWith('video/') ? <Video size={16} /> : <File size={16} />}
                  </div>
                  <div className={styles.selectedFileInfo}>
                    <div className={styles.selectedFileName}>{file.name}</div>
                    <div className={styles.selectedFileSize}>{this.formatFileSize(file.size)}</div>
                  </div>
                  <button type="button" className={styles.removeFileButton} onClick={() => this.handleRemoveFile(index)} title="Remove">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={this.handleSendMessage} className={styles.chatInput}>
          <input
            ref={this.fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            onChange={this.handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className={styles.attachButton}
            onClick={() => this.fileInputRef.current?.click()}
            title="Attach files"
            disabled={isSending || selectedFiles.length >= 10}
          >
            <Paperclip size={20} />
          </button>
          <input
            type="text"
            value={inputMessage}
            onChange={this.handleInputChange}
            placeholder={replyingTo ? `Reply to: ${replyingTo.message.slice(0, 30)}...` : 'Type your message...'}
            className={styles.inputField}
            disabled={isSending}
          />
          <button
            type="submit"
            className={styles.sendButton}
            disabled={(!inputMessage.trim() && selectedFiles.length === 0) || isSending || isUploading}
            title={isUploading ? 'Uploading...' : 'Send'}
          >
            {isSending || isUploading ? <div className={styles.sendSpinner} /> : <Send size={20} />}
          </button>
        </form>
      </div>
    );
  }
}

export default TournamentChat;
